import React from 'react';
import { styled } from '@mui/material/styles';
import { Box, InputBase, IconButton, Paper } from '@mui/material';
import { Search as SearchIcon, X as CloseIcon } from 'lucide-react';
import AGTFoundService from '../services/AGTUps-product-services/AGTFound/AGTFoundService';
import { useTranslation } from 'react-i18next';

const appleColors = {
  light: {
    label: { primary: '#000000' },
    system: {
      grey: '#8E8E93',
      grey2: '#AEAEB2',
      grey3: '#C7C7CC',
      grey6: '#F2F2F7',
      inputBg: '#F2F2F7',
      green: '#31C859',
      white: '#FFFFFF',
      black: '#000000'
    },
    bg: { 
      primaryElevated: '#FFFFFF',
      secondary: '#F2F2F7',
      tertiary: '#FAFAFA'
    },
    fill: { tertiary: '#767680' },
    ui: { accent: '#007BFE', base: '#F2F2F7' }
  },
  dark: {
    label: { primary: '#FFFFFF' },
    system: {
      grey: '#8E8E93',
      grey2: '#636366',
      grey3: '#464649',
      grey6: '#1C1C1E',
      inputBg: '#1C1C1E',
      green: '#2DD257',
      white: '#FFFFFF',
      black: '#000000'
    },
    bg: { 
      primaryElevated: '#000000',
      secondary: '#1C1C1E',
      tertiary: '#2C2C2E'
    },
    fill: { tertiary: '#767680' },
    ui: { accent: '#0385FF', base: '#272729' }
  }
};

const navigationColors = (theme) => {
  const mode = theme.palette.mode;
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const c = mode === 'light' ? appleColors.light : appleColors.dark;
  return {
    background: c.bg.primaryElevated,
    backgroundSecondary: c.bg.secondary,
    backgroundTertiary: c.bg.tertiary,
    textDefault: c.label.primary,
    textSecondary: c.system.grey,
    cardBackground: c.system.grey6,
    inputBackground: c.system.inputBg,
    borderColor: c.system.grey3,
    accent: isWallpaperMode ? '#FFFFFF' : c.ui.accent,
    success: c.system.green,
    error: '#FF3B30',
    warning: '#FF9500'
  };
};

const SearchWrapper = styled(Paper)(({ theme }) => {
  const colors = navigationColors(theme);
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    maxWidth: '1200px',
    margin: '0 auto',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : colors.cardBackground,
    borderRadius: theme.shape.borderRadius * 8,
    padding: theme.spacing(0.5, 2),
    transition: 'all 0.3s ease',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    '&:focus-within': {
      backgroundColor: isWallpaperMode
        ? 'rgba(128, 128, 128, 0.35)'
        : colors.cardBackground,
    },
  };
});

const SearchInput = styled(InputBase)(({ theme }) => ({
  flex: 1,
  // En lugar de un color fijo, usamos el primario del texto según el tema
  color: theme.palette.text.primary,
  fontSize: '1.1rem',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1.5, 1, 1.5, 0),
    '&::placeholder': {
      // El placeholder también lo tomamos del text.primary con opacidad reducida
      color: theme.palette.text.primary,
      opacity: 0.5,
    },
  },
}));

const ClearButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
  // Para íconos, podemos usar un color "degradado" o un color del theme,
  // ajusta a tu gusto si quieres mayor contraste
  color: theme.palette.text.secondary,
}));

const AGTFound = ({ 
  placeholder,
  value = '',
  onChange = () => {},
  onResults = () => {},
  setIsSearching = () => {}
}) => {
  const { t } = useTranslation();
  
  // Usar el placeholder proporcionado o el predeterminado traducido
  const finalPlaceholder = placeholder || t('agtUps.common.search.placeholder');

  const handleSearch = async () => {
    if (!value.trim()) return;

    setIsSearching(true);
    onResults({ recommendations: [] });

    try {
      const result = await AGTFoundService.search(value);
      onResults(result);
    } catch (error) {
      console.error('Error en búsqueda:', error);
      onResults({ error: 'No se pudo procesar tu búsqueda' });
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSearch();
    }
  };

  const handleClear = () => {
    onChange('');
    onResults({ recommendations: [] });
  };

  return (
    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
      <SearchWrapper elevation={0}>
        <SearchInput
          placeholder={finalPlaceholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyPress={handleKeyPress}
          fullWidth
        />
        <IconButton onClick={handleSearch} aria-label="search">
          <SearchIcon size={22} />
        </IconButton>
        {value && (
          <ClearButton 
            onClick={handleClear}
            size="small"
            aria-label="clear search"
          >
            <CloseIcon size={18} />
          </ClearButton>
        )}
      </SearchWrapper>
    </Box>
  );
};

export default AGTFound;